import { isPublicEmailProvider } from "@/lib/string";
import React from "react";
import Button from "@components/library/Button";
import { LoadingIcon } from "@components/library/Icons";
import { hlToast, ToastVariant } from "@components/library/Toast";
import { FormikTextInput } from "@components/molecules/FormikInput";
import { Form, Formik } from "formik";
import { PASSWORD_VALIDATION } from "lib/user";
import Link from "next/link";
import * as Yup from "yup";
import { ChevronLeftIcon } from "@heroicons/react/solid";
import IconButton from "@components/library/IconButton";
import { LOGIN_URL } from "@/lib/constants";

interface FormikValues {
  email_address: string;
  full_name: string;
  password: string;
}

interface Props {
  initialValues?: FormikValues;
  immutableEmail?: boolean;
  onSubmit: (values: FormikValues) => Promise<void>;
  onBackClick: () => void;
}

const SignupForm = ({ initialValues, immutableEmail, onSubmit, onBackClick }: Props) => {
  return (
    <Formik
      enableReinitialize
      initialValues={initialValues || { email_address: "", full_name: "", password: "" }}
      validationSchema={Yup.object({
        email_address: Yup.string().email("Invalid email address").required("Required"),
        full_name: Yup.string().min(1, "Required").required("Required"),
        password: PASSWORD_VALIDATION,
      })}
      onSubmit={async (values, { setSubmitting, setStatus }) => {
        setSubmitting(true);
        try {
          await onSubmit(values);
        } catch (e: any) {
          console.log(e);
          if (e.response?.data?.detail?.type === "value_error.uniqueness") {
            setStatus({ error: "Email already used" });
          } else {
            const msg = e.response.data?.detail?.msg || "Failed to create account";
            const description = e.response.data?.detail?.description || "Please contact us for assistance.";
            hlToast({ title: msg, description, variant: ToastVariant.Error });
          }
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({ isSubmitting, isValid, values, status, setStatus }) => (
        <Form className="flex h-full grow flex-col justify-between gap-32" autoComplete="off">
          {/* Form */}
          {/* Fields wrap */}
          <div className="flex flex-col gap-24">
            <div className="flex flex-col gap-24">
              <FormikTextInput
                name="email_address"
                label="Work email"
                placeholder={!immutableEmail ? "you@company.com" : ""}
                type="email"
                disabled={immutableEmail}
                // Additional error message so we don't block submission
                additionalError={
                  !immutableEmail && isPublicEmailProvider(values.email_address)
                    ? "Please use your company email address"
                    : undefined
                }
                privacyLevel="moderate"
              />
              <FormikTextInput name="full_name" label="Full name" autoComplete="name" privacyLevel="moderate" />
              <FormikTextInput
                name="password"
                label="Password"
                type="password"
                autoComplete="new-password"
                privacyLevel="high"
              />
            </div>
          </div>

          <div className="flex w-full flex-col items-center gap-24">
            {status?.error && (
              <div className="-mb-6 flex w-full animate-fade-in-down items-center justify-between rounded border border-oldred-300 bg-oldred-100 px-16 py-12">
                <div className="text-14-20 text-oldred-800">
                  Email taken. Did you mean to{" "}
                  <Link className="underline hover:text-oldred-600" href={LOGIN_URL}>
                    log in
                  </Link>
                  ?
                </div>
              </div>
            )}
            <Button
              size={40}
              className="w-full"
              styling="solid"
              type="submit"
              shade="black"
              disabled={!isValid || isSubmitting}
              loading={isSubmitting}
              IconLeft={isSubmitting ? LoadingIcon : undefined}
            >
              {isSubmitting ? "Creating account..." : "Create account"}
            </Button>
            {!isSubmitting && (
              <div className="absolute left-24 top-24">
                <IconButton onClick={onBackClick} Icon={ChevronLeftIcon} className="text-oldgray-600" size={32} />
              </div>
            )}
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default SignupForm;
